/* eslint-disable no-storage/no-browser-storage */
import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import intl from '@frameio/i18n/intl';
import { toSupportedLocaleOrDefault } from '@frameio/shared/utils/toSupportedLocale';
import { useGetWhatsNewFeedLastUpdatedTimeQuery } from './gql/queries/generated/GetWhatsNewFeedLastUpdatedTime';
import {
  readLastViewedTimeFromLocalStorage,
  saveLastViewedTimestampToLocalStorage,
} from './utils';

export type ListItem = {
  title: string;
  type: string;
  link: string;
  date: string;
  description?: string;
  imageUrl?: string;
  id: string;
};

type WhatsNewContextType = {
  setLastUpdatedTime: (time: number) => void;
  setLastViewedTime: (time: number) => void;
  hasUpdatesSinceLastViewed: () => boolean;
};

const WhatsNewContext = createContext<WhatsNewContextType | undefined>(
  undefined
);

export const WhatsNewProvider = ({ children }: { children: ReactNode }) => {
  const locale = toSupportedLocaleOrDefault(intl().locale);
  // Get the last updated time from the server
  const { data: lastUpdatedTimeData } = useGetWhatsNewFeedLastUpdatedTimeQuery({
    variables: {
      locale,
    },
  });
  const [lastUpdatedTime, setLastUpdatedTime] = useState<number | null>(null);
  const [lastViewedTime, setLastViewedTime] = useState<number | null>(null);

  const hasUpdatesSinceLastViewed = useCallback(() => {
    // if lastViewedTime changed, save it to local storage
    if (lastViewedTime) saveLastViewedTimestampToLocalStorage(lastViewedTime);

    const savedLastViewedTime =
      readLastViewedTimeFromLocalStorage() || lastViewedTime;

    // if lastUpdatedTime is not set, return to not show the notification badge
    if (!lastUpdatedTime) {
      return false;
    }

    if (!savedLastViewedTime) {
      // if lastViewedTime is not set, return to show the notification badge
      return true;
    }

    return savedLastViewedTime < lastUpdatedTime;
  }, [lastUpdatedTime, lastViewedTime]);

  useEffect(() => {
    // Get the last time data was fetched from sanity and saved on the server
    const lastUpdated = lastUpdatedTimeData?.whatsNewFeed?.lastUpdated;
    if (lastUpdated) {
      const lastUpdatedDate = new Date(lastUpdated);
      const lastUpdatedTimestamp = lastUpdatedDate.getTime();
      setLastUpdatedTime(lastUpdatedTimestamp);
    }
  }, [lastUpdatedTimeData, lastViewedTime]);

  // Memoize the context value
  const value = useMemo(
    () => ({
      setLastUpdatedTime,
      setLastViewedTime,
      hasUpdatesSinceLastViewed,
    }),
    [setLastUpdatedTime, setLastViewedTime, hasUpdatesSinceLastViewed]
  );

  return (
    <WhatsNewContext.Provider value={value}>
      {children}
    </WhatsNewContext.Provider>
  );
};

// Custom hook to use the WhatsNewContext
export const useWhatsNew = () => {
  const context = useContext(WhatsNewContext);
  if (context === undefined) {
    throw new Error('useWhatsNew must be used within a WhatsNewProvider');
  }
  return context;
};
